import {
  type AxiosRequestConfig,
  publicHttpClient,
} from '@ahm/api-wrappers-core';
import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import { apiRoutes } from '../api-routes';
import { CACHE_STRATEGIES } from '@/lib/react-query/query-client';

export interface FoodOrderCategoriesResponse {
  categories: FoodOrderCategory[];
}

export interface FoodOrderCategory {
  id: number;
  projects: string;
  description_en_us: string;
  description_vi_vn: string;
  enable: boolean;
  icon_img_url: string;
  order: number;
  slug: string;
  title_en_us: string;
  title_vi_vn: string;
  update_at: number;
}

export interface GetFoodOrderCategoriesParams {
  skip?: number;
  limit?: number;
  type?: 'STORE' | 'SOLO_MEAL';
}

export const getFoodOrderCategoriesFn = async (
  params: GetFoodOrderCategoriesParams = {},
  config: AxiosRequestConfig = {}
) => {
  const url = apiRoutes.ahafood.store.get_category;
  const response = await publicHttpClient.get<FoodOrderCategoriesResponse>(
    url,
    {
      params: {
        type: 'STORE',
        ...params,
      },
      ...config,
    }
  );

  return response?.data;
};

export const useGetFoodOrderCategories = (
  params: GetFoodOrderCategoriesParams = {},
  opts?: Omit<
    UseQueryOptions<FoodOrderCategoriesResponse, Error>,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery<FoodOrderCategoriesResponse, Error>({
    queryKey: [apiRoutes.ahafood.store.get_category, params],
    queryFn: () => getFoodOrderCategoriesFn(params),
    ...CACHE_STRATEGIES.static, // 30min cache - categories rarely change
    ...opts, // opts can override cache strategy if needed
  });
};

// Alias cho Solo Meal để backward compatibility
export const useGetSolomealCategory = (
  params: Omit<GetFoodOrderCategoriesParams, 'type'> = {},
  opts?: Omit<
    UseQueryOptions<FoodOrderCategoriesResponse, Error>,
    'queryKey' | 'queryFn'
  >
) => {
  return useGetFoodOrderCategories({ ...params, type: 'SOLO_MEAL' }, opts);
};

// Export aliases cho backward compatibility
export type Category = FoodOrderCategory;
export type SolomealCategoryResponse = FoodOrderCategoriesResponse;
export type GetSolomealCategoryParams = GetFoodOrderCategoriesParams;
